import React, { useState } from "react";
import { Card, CardHeader, CardContent, FormControlLabel, Link, Switch, Typography } from "@mui/material";
import RxSelector from "./RxSelector";
import { observer } from "mobx-react";
import useStore from "../../../helpers/useStore";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { useRxsForPatient } from "../../../stores/Rx/rx.queries";
import { PatientCommands } from "../../../stores/Patients/patient.commands";
import { RxLightDto, RxLightDtoProcessingStatusEnum } from "../../../api-client";
import moment from "moment";

/** Minimum number of Rezepte that stay visible before old ones get hidden. */
const MIN_VISIBLE_RXS = 5;

/** Statuses of old Rxs that are no longer active and may be hidden. */
const isInactiveRx = (rx: RxLightDto) =>
  rx.processingStatus === RxLightDtoProcessingStatusEnum.Abgerechnet ||
  rx.processingStatus === RxLightDtoProcessingStatusEnum.Abgebrochen;

/**
 * Returns the ids of old (abgerechnet/abgebrochen) Rxs to hide.
 * Expects `rxs` sorted oldest first. Rxs with any other status are never
 * hidden, and at least MIN_VISIBLE_RXS Rxs remain visible in total,
 * preferentially hiding the oldest inactive ones.
 */
export const getHiddenOldRxIds = (rxs: RxLightDto[]): Set<string> => {
  const maxHideable = rxs.length - MIN_VISIBLE_RXS;
  const hidden = new Set<string>();
  if (maxHideable <= 0) return hidden;
  for (const rx of rxs) {
    if (hidden.size >= maxHideable) break;
    if (isInactiveRx(rx)) hidden.add(rx.id);
  }
  return hidden;
};

export const RxSelectorCard: React.FC = observer(() => {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [showOldRxs, setShowOldRxs] = useState(false);
  const patientsStore = useStore(PatientsContext);
  const patientId = patientsStore.selectedPatientId;
  // const selectedRxId = patientsStore.selectedRxId;

  const { data: rxDataFull = [], isLoading } = useRxsForPatient(patientId!);
  const rxData = includeDeleted ? rxDataFull : rxDataFull.filter((rx: any) => !rx.deletedAt);
  rxData.sort((a: RxLightDto, b: RxLightDto) => {
    // Handle cases where createdAt might be undefined
    if (!a.createdAt && !b.createdAt) return 0;
    if (!a.createdAt) return 1; // a comes after b
    if (!b.createdAt) return -1; // a comes before b

    // Parse the date strings and compare them (oldest first)
    return moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf();
  });

  // Hide old (abgerechnet/abgebrochen) Rxs unless the user opted to see them
  const hiddenRxIds = getHiddenOldRxIds(rxData);
  const visibleRxData = showOldRxs
    ? rxData
    : rxData.filter((rx: RxLightDto) => !hiddenRxIds.has(rx.id));
  const hiddenRxCount = hiddenRxIds.size;

  const { selectRx } = PatientCommands(patientsStore);

  const handleRxSelect = (rx: any) => {
    selectRx(rx.id);
  };

  // Find the selected RX object to pass to RxSelector
  // const selectedRx = rxData.find((rx: RxLightDto) => rx.id === selectedRxId);

  return (
    <Card sx={{ padding: "0px" }}>
      <CardHeader
        title="Rezepte"
        action={
          <FormControlLabel
            sx={{ width: "80" }}
            control={
              <Switch
                checked={includeDeleted || false}
                onChange={(e) => {
                  setIncludeDeleted(e.target.checked);
                }}
              />
            }
            label="Gelöschte Rx"
          />
        }
      />
      <CardContent sx={{ padding: "0px" }}>
        {isLoading && <Typography>Laden...</Typography>}
        {!isLoading && rxData.length === 0 && <Typography>keine Rezepte gefunden</Typography>}
        {!isLoading && hiddenRxCount > 0 && (
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={() => setShowOldRxs((prev) => !prev)}
            sx={{ display: "block", width: "100%", textAlign: "center", padding: "8px 16px 0px" }}
          >
            {showOldRxs
              ? "Alte Rezepte ausblenden"
              : `Alte Rezepte einblenden (${hiddenRxCount})`}
          </Link>
        )}
        {!isLoading && rxData.length > 0 && (
          <RxSelector
            data={visibleRxData}
            selectedRxId={patientsStore.selectedRxId || undefined}
            handleRowClick={handleRxSelect}
          />
        )}
      </CardContent>
    </Card>
  );
});
